/**
 * Tipos de payloads del backend.
 *
 * Espejo de los serializers del backend (backend/app/blueprints/auth/services.py
 * `serialize_user` + responses de routes.py). Sprint futuro: generar desde
 * Pydantic/dataclasses Python automáticamente para evitar drift.
 */

export interface User {
  id: number;
  email: string;
  full_name: string;
  role: "athlete" | "coach" | "admin";
  locale: string;
  timezone: string;
  email_verified: boolean;
  onboarding_completed: boolean;
  created_at: string | null;
  /**
   * Sprint 14 S14.5 (R3 GDPR): si el usuario tiene contraseña (nativo) o es
   * solo-OAuth. Determina la confirmación del borrado de cuenta (reauth con
   * contraseña vs confirm explícito). Opcional por resiliencia de fixtures.
   */
  has_password?: boolean;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
}

export interface AuthSuccess extends TokenPair {
  user: User;
}

/**
 * Estado de la conexión Strava del user. Espejo de
 * backend/app/blueprints/strava/routes.py:strava_status.
 *
 * - `connected` = true sólo si hay OAuthConnection con
 *   disconnected_at IS NULL.
 * - `activities_count` cuenta SOLO actividades con external_source='strava'
 *   (no actividades manuales ni de otros proveedores futuros). Se preserva
 *   incluso cuando connected=false (soft-delete de la conexión no borra
 *   histórico).
 */
export interface StravaStatus {
  connected: boolean;
  last_synced_at: string | null;
  activities_count: number;
  disconnected_at: string | null;
}

/**
 * Estado de un job de importación Strava (Sprint C Bloque 2).
 * Espejo de backend `StravaImportJob.to_dict()`.
 *
 * Lifecycle: pending → started (worker la cogió) → completed | failed.
 * El frontend hace polling a /strava/import_status cada 3s mientras
 * status sea pending o started.
 */
export interface StravaImportStatus {
  job_id: number;
  task_id: string;
  status: "pending" | "started" | "completed" | "failed";
  job_type: "initial_import" | "sync";
  activities_imported: number;
  error_message: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string | null;
}

/** Respuesta del POST /strava/sync cuando se encola un nuevo job (202). */
export interface StravaSyncEnqueued {
  job_id: number;
  status: "pending";
}

/** Respuesta 409 cuando ya hay un import activo. */
export interface StravaSyncInProgress {
  error: "import_already_in_progress";
  job_id: number;
  status: "pending" | "started";
}

// =====================================================================
// Race targets (v15.5-§8.3)
// Espejo de backend models/race_target.py + serializer en
// app/blueprints/races/routes.py:_serialize_race_target.
// =====================================================================

/**
 * Carrera registrada por el atleta. Hasta 5 por user. La marcada
 * `is_primary=true` ancla la periodización del plan; las demás son
 * informativas.
 */
/** v16.3 §1.2 + P2: clima reportado en el form opcional del día de carrera. */
export type Weather = "calor" | "frio" | "viento" | "lluvia" | "normal";

export interface RaceTarget {
  id: number;
  /** Opcional — atleta puede dejar sin nombre (UI muestra fallback). */
  name: string | null;
  /** ISO `YYYY-MM-DD`. Backend garantiza fecha futura al crear/editar. */
  race_date: string;
  /** Enum lowercase: 5k|10k|15k|20k|half_marathon|marathon|trail_short|trail_long. */
  distance: string;
  /** Enum lowercase: asfalto|trail|mixto. */
  surface: string;
  /** Máximo 1 primary por user (unique partial index en BD). */
  is_primary: boolean;
  /** True si el atleta rellenó cualquier campo del form de resultado. */
  completed: boolean;
  /** v16.3 B.1 — formulario opcional del día de carrera. Null si el atleta
   *  no rellenó ese campo. Si llega cualquiera vía PATCH, el backend marca
   *  `completed=true` automáticamente. */
  actual_time_seconds?: number | null;
  actual_distance_km?: number | null;
  actual_avg_hr?: number | null;
  actual_max_hr?: number | null;
  /** Sensaciones 1-5 (1=mal, 5=excelente). */
  actual_feeling?: number | null;
  actual_weather?: Weather | null;
}

/** Respuesta de GET /races. Ordenada por race_date asc en el backend. */
export interface RacesResponse {
  races: RaceTarget[];
}

/** Body de POST /races. `name` opcional; resto requeridos (backend valida). */
export interface CreateRacePayload {
  name?: string | null;
  race_date: string;  // ISO YYYY-MM-DD
  distance: string;
  surface: string;
}

/** Body de PATCH /races/:id. Todos los campos opcionales (parcial). NO
 *  incluye `is_primary` — el backend lo rechaza explícitamente con
 *  `cannot_change_is_primary_here` (la promoción es POST /races/:id/promote
 *  en §8.3.E). */
export interface UpdateRacePayload {
  name?: string | null;
  race_date?: string;
  distance?: string;
  surface?: string;
}

/** v16.3 B.2 — body de PATCH /races/:id cuando se envía el formulario
 *  opcional del día de carrera. Solo los campos rellenos por el atleta.
 *  Si llega CUALQUIERA, el backend marca `completed=true` automático. */
export interface RaceResultPayload {
  actual_time_seconds?: number;
  actual_distance_km?: number;
  actual_avg_hr?: number;
  actual_max_hr?: number;
  actual_feeling?: number;  // 1-5
  actual_weather?: Weather;
}

/** Respuesta de POST/PATCH /races[/:id]. */
export interface RaceTargetResponse {
  race_target: RaceTarget;
}

/** Respuesta de POST /races/:id/promote (v15.5-§8.3.E).
 *
 * Espejo de backend/app/blueprints/races/routes.py:promote_race_target.
 *
 * Casos del payload:
 * - `was_already_primary=true` (idempotente): el resto de flags vienen
 *   en estado neutro (`plan_regenerated=false`, *_plan_id=null). El
 *   backend NO regenera nada.
 * - `was_already_primary=false` + `plan_regenerated=true`: swap aplicado
 *   y plan activo regenerado. `replaced_plan_id` = id del plan viejo
 *   (superseded + soft-deleted); `new_plan_id` = id del plan nuevo.
 * - `was_already_primary=false` + `plan_regenerated=false`: user sin
 *   plan activo. Solo se hizo el swap. *_plan_id quedan null.
 *
 * En error (400 plan_regeneration_failed): el backend hace rollback
 * COMPLETO (verificado empíricamente en test_v15_5_8_3_e_plan_*). El
 * estado de la BD queda como antes del POST. El frontend muestra el
 * `detail` del error inline sin cerrar el modal. */
export interface PromoteRaceResponse {
  race_target: RaceTarget;
  was_already_primary: boolean;
  plan_regenerated: boolean;
  new_plan_id: number | null;
  replaced_plan_id: number | null;
}


// =====================================================================
// Training plans (Sprint 6 Bloque 4)
// Espejo de backend models/training_*.py + serializers en
// app/blueprints/training_plans/routes.py.
// =====================================================================

export type SessionType =
  | "z2"
  | "z2_fartlek"
  | "caco_z2"
  | "caco_plus"
  | "series_cortas"
  | "series_medias"
  | "tempo_continuo"
  | "larga_progresiva"
  // v15.3: rodaje continuo Z3 por sensaciones (3 días ppal sin reloj).
  // Tipo nuevo, aún no colocado en secuencias (v15.2).
  | "rodaje_z3"
  // v15.2b.1 (brief v15.4 §3): tempo largo con bloques alternados
  // Z2-Z3 (70%) / Z4 (30%). Día 4 del 4d-maratón. Distancia base 16 km;
  // progresión por ocurrencia + tramo en v15.prog.2.
  | "tempo_largo"
  // v15.2-taper (brief v15.4 §4): sesión COMBINADA Z2 + tempo Z3-Z4 en
  // el mismo entrenamiento. Solo se coloca en taper de 1 día/semana.
  | "taper_combo"
  // v15.4-tipo (brief v15.4 §7): "Series de cuesta" — sesión NUEVA y
  // SEPARADA de la "Cuestas clásica" (caco_plus). 1 min × reps, pendiente
  // 6-14%, trail amateur exclusivamente. Base 6 reps; la progresión
  // 6→8→10 vivirá en v15.prog.1.
  | "cuestas_v15"
  | "rest"
  // Sprint 6 Bloque 5: TEST-VAM se inyecta como sesión del plan en día 3
  // + cada 3 meses si el atleta tiene reloj (device_type != 'mobile_only').
  | "test_vam"
  // Sprint 9 S9.7 (motor v7.1 §11 "Descanso activo siempre"): templates
  // de recuperación post-calidad. Backend los emite cuando una rest del
  // plan cae inmediatamente tras una sesión de calidad. También Sprint
  // 10 S10.2 los usa como sustituta de "Reportar fatiga" (rod_z2_corto
  // 30 min en mismo día que la original skipped).
  // v13 §1: `caco_recovery` eliminado (ya no se inyecta descanso activo).
  // `rod_z2_corto` se conserva (sustituta por fatiga, mismo día).
  | "rod_z2_corto"
  // Sprint 10 S10.3 (motor v7.1 §6): rodaje largo continuo Z2 sobre trail,
  // inyectado por `inject_long_run_trail` en planes con surface trail/mixto.
  // Fix (detectado en v15.3): faltaba en el union → renderizaba con label
  // en blanco para atletas de trail.
  | "long_run_trail"
  // Pre-flight F16: rodaje suave por sensaciones (zone-free) inyectado el
  // día 1 antes del TEST-VAM para amateur+reloj. intensity_zone siempre null.
  | "rodaje_previo"
  // v16.3 C.1 (Q1 Alberto): activación pre-carrera del día previo a race_date.
  // Inyectada por gap-fill (G.1, backend) cuando hay hueco entre fin del
  // taper y race_date. Estructura fija 25 min Z2 suave + 4 progresivos 30s.
  | "activacion_pre_carrera";

export type CompletionStatus = "planned" | "completed" | "partial" | "skipped";

/** v16.3 §G.1 + v16.4 §1: phase `race_week` añadida como gap-fill entre
 *  el fin del taper y race_date. Contiene N sesiones (1 <= N <= 6):
 *  descansos los días intermedios + activacion_pre_carrera el día previo
 *  a race_date. Solo aparece en planes plan_type='race' cuando race_date
 *  no cae en lunes (gap >= 1). NO cuenta para `plan.total_weeks`.
 *  El frontend la separa de las regulares para navegación y la renderiza
 *  como sección dedicada vía RaceWeekSection. */
export type TrainingPhase =
  | "acumulacion"
  | "intensificacion"
  | "peak"
  | "taper"
  | "race_week";

/** Razón estructurada del skip (Sprint 10 S10.2 / cierra TRACKING-001).
 *
 * - `logistics` / `physical` → atleta vía PATCH action=skip.
 * - `fatigue` → atleta vía PATCH action=report_fatigue (la sub se crea
 *   en mismo session_date, ver `WeekView` para detección dual).
 * - `auto_postpone_taper` → motor auto-skip al posponer cuando overflow
 *   vs race_date (S10.1).
 * - null → sesión planned/completed/partial, o legacy pre-S10.2.
 *
 * Optional en el type (`?:`) para que tests con fixtures legacy sin
 * el campo sigan compilando — el backend siempre emite el campo
 * (null cuando aplica), pero el contrato consumer es resiliente. */
export type SkipReason =
  | "logistics"
  | "physical"
  | "fatigue"
  | "auto_postpone_taper";

/** v16.1 §8: kind del TEST-VAM persistido en la sesión. Solo aplica si
 *  `session_type === 'test_vam'`. Valores:
 *  - 'initial'  — único kind emitido por el motor desde v16.1 §8 sub-bloque 2.
 *  - 'periodic' — LEGACY. Planes pre-v16.1 backfilleados (sub-bloque 1).
 *                 El motor ya no emite este kind.
 *  - 'final'    — FUTURO. Reservado para recovery week post-carrera.
 *  - null       — sesión no es test_vam, o legacy sin backfill.
 *  Opcional en el type (`?:`) para que fixtures legacy sin el campo
 *  sigan compilando — el backend siempre emite el campo (null cuando
 *  aplica) tras sub-bloque 3, pero el contrato consumer es resiliente. */
export type VamTestKind = "initial" | "periodic" | "final";

export interface TrainingSession {
  id: number;
  session_date: string; // ISO date
  session_type: SessionType;
  /** v16.1 §8: kind del TEST-VAM. Ver type VamTestKind arriba. */
  vam_test_kind?: VamTestKind | null;
  intensity_zone: string | null; // 'Z2' | 'Z3' | 'Z4' | 'Z5' | 'mixed' | null
  distance_target_km: number | null;
  duration_target_min: number | null;
  session_structure_json: {
    warmup?: string | null;
    main?: string | null;
    cooldown?: string | null;
  } | null;
  completion_status: CompletionStatus;
  completed_at: string | null;
  actual_activity_id: number | null;
  notes: string | null;
  /** Sprint 10 S10.2 — null si no skipped o si skipped legacy. */
  skip_reason?: SkipReason | null;
  /** v14 §3 — rango de ritmo objetivo "m:ss–m:ss /km" (em-dash) ya
   *  formateado por el backend, o null si N/A (sin VAM medida, zona
   *  mixed/null, o test_vam). El backend YA aplica el gate de VAM; el
   *  gate de has_watch se aplica en el RENDER (ver SessionCard). */
  pace_range?: string | null;
  /** v14_2 §8 — resultados reales reportados al completar (modal manual,
   *  rama SIN Strava). Null si no se reportaron. `actual_avg_pace_seconds`
   *  en segundos/km. `perceived_effort` 1-5. */
  actual_avg_hr?: number | null;
  actual_max_hr?: number | null;
  actual_avg_pace_seconds?: number | null;
  perceived_effort?: number | null;
  feeling?: SessionFeeling | null;
  /** v16.3 §4 — etiqueta INFORMATIVA de superficie recomendada por sesión.
   *  Valores canónicos (string opaco para el frontend, traducido vía
   *  `labelForRecommendedSurface`):
   *  - 'asfalto' | 'montana' | 'pista_o_asfalto' | 'pista_o_llano' | 'free'.
   *  Null si plan_surface no resoluble, session_type='rest', o tipo no
   *  mapeado. El reparto mod-4 caco_plus en plan 'mixto' se decide en
   *  backend según week_number (§4.2 nota 2). */
  recommended_surface?: string | null;
}

export interface TrainingWeek {
  id: number;
  week_number: number;
  start_date: string; // ISO date — siempre lunes (convención Nexum)
  phase: TrainingPhase;
  volume_target_km: number;
  sessions: TrainingSession[];
}

/** Sprint 13 S13.2/S13.4 (motor v10): tipo de plan.
 *  - 'race'        → Plan de Carrera Objetivo (race_date real).
 *  - 'progression' → Plan de Progresión v10 §2 (12 sem, sin carrera real).
 *  - 'recovery'    → Semana de recuperación post-plan v10 §1. */
export type PlanType = "race" | "progression" | "recovery";

/** Sprint 13 S13.2 (motor v10): estado del ciclo de vida del plan.
 *  - 'active'      → plan vigente.
 *  - 'completed'   → plan terminado naturalmente (race_date pasada para
 *                    race; vencimiento natural para progression/recovery).
 *                    El plan race transiciona aquí automáticamente y
 *                    encadena recovery week (v16.3 §1.3).
 *  - 'superseded'  → plan reemplazado por regeneración. */
export type PlanStatus = "active" | "completed" | "superseded";

export interface TrainingPlan {
  id: number;
  user_id: number;
  race_distance: string;
  race_date: string; // ISO date
  /** Sprint 13 S13.2 (motor v10): tipo de plan. El backend lo serializa
   *  siempre (server_default 'race'). El frontend lo usa para distinguir
   *  visualmente (badge) y para el flujo post-recovery (S13.6b). */
  plan_type: PlanType;
  /** Sprint 13 S13.2 (motor v10): estado del plan. Opcional en el tipo
   *  por resiliencia; el backend lo emite siempre (default 'active'). */
  status?: PlanStatus;
  /** Sprint 11 S11.1 (motor v8 §1): fecha de inicio del plan elegida
   *  por el atleta en el form de create-plan. Pre-S11.1 esta fecha se
   *  derivaba en runtime como `_next_monday(today)` y no se persistía;
   *  ahora viene del backend siempre. Migration `e4e9ac29f972` backfill
   *  planes legacy desde `DATE(created_at)`. */
  start_date: string; // ISO date YYYY-MM-DD
  total_weeks: number;
  weeks_acumulacion: number;
  weeks_intensificacion: number;
  weeks_taper: number;
  generation_warning: string | null;
  created_at: string;
  weeks: TrainingWeek[];
  /** Sprint 11 S11.5 (motor v8 §3): flag transitorio emitido por
   *  GET /training-plans/active cuando el backend acaba de reprogramar
   *  un test_vam missed (+4 días). NO persiste en BD — al siguiente
   *  GET, si no hay missed test, no se emite. El frontend lo usa para
   *  mostrar un banner one-shot al atleta. */
  recent_vam_reschedule?: {
    session_id: number;
    old_date: string; // ISO date
    new_date: string; // ISO date
  };
}

/**
 * Recommendation del adapter del motor — el backend la calcula lazy en
 * cada GET /training-plans/active. Tipos posibles:
 * - 'fatigue_recovery_week': 2 de 3 señales de fatiga detectadas.
 * - 'consecutive_incomplete_weeks': 2 sem consecutivas con <50% completadas.
 *
 * El frontend SÓLO formatea — el copy del `message` ya viene del backend.
 */
export interface TrainingRecommendation {
  type: "fatigue_recovery_week" | "consecutive_incomplete_weeks" | string;
  message: string;
  details?: Record<string, unknown>;
}

/** Forma CRUDA del response de GET /training-plans/active (snake_case,
 *  espejo del backend). `getActivePlan` la parsea a `ActivePlanResult`. */
export interface ActivePlanResponse {
  plan: TrainingPlan | null;
  recommendations: TrainingRecommendation[];
  /** Sprint 13 S13.4 (motor v10 §1): true cuando la semana de
   *  recuperación terminó y el atleta debe elegir entre nueva carrera o
   *  Plan de Progresión. */
  post_recovery_options_available?: boolean;
  /** Sprint 13 S13.8 (motor v10.1 §2): true cuando una progresión venció
   *  sin carrera y el atleta debe elegir distancia para la siguiente. */
  progression_distance_choice_pending?: boolean;
  /** v16.5.X T1 4b sub-paquete X: true tras la transición race→recovery
   *  (race+1), persistente hasta dismiss o expiración 30d. */
  race_just_finished?: boolean;
}

/** Sprint 13 S13.10a: forma PARSEADA que devuelve `getActivePlan`
 *  (camelCase, `plan` nullable). Expone los flags que llegan justamente
 *  cuando `plan === null` (recovery terminada / progresión vencida) — que
 *  el shape antiguo (`TrainingPlan | null`) perdía. */
export interface ActivePlanResult {
  plan: TrainingPlan | null;
  recommendations: TrainingRecommendation[];
  postRecoveryOptionsAvailable: boolean;
  progressionDistanceChoicePending: boolean;
  /** v16.5.X T1 4b sub-paquete X (Alberto 19/06/26): True cuando el
   *  atleta acaba de transicionar de race → recovery (race+1) y el
   *  banner `PostRaceImmediateBanner` debe renderizarse encima del plan
   *  recovery activo. Dismissable con re-aparición a las 24h.
   *  Coexiste con `postRecoveryOptionsAvailable` — éste se activa a los
   *  race+8 días (recovery completada), aquél a los race+1 (recovery
   *  acaba de crearse). */
  raceJustFinished: boolean;
}

export interface CurrentWeekResponse {
  week: TrainingWeek;
}

/** Sprint 13 S13.9/S13.10b: body de POST /training-plans/active/add-race.
 *  `surface` default 'asfalto'. `confirm_trail_modality` se reenvía a true
 *  tras el 409 `trail_modality_confirmation_required` (perfil asfalto +
 *  carrera trail → confirmar pasar a modalidad mixto). */
export interface AddRacePayload {
  race_distance: string;
  race_date: string; // ISO date YYYY-MM-DD
  surface?: "asfalto" | "trail";
  confirm_trail_modality?: boolean;
}

/** Respuesta 201 de add-race. `surface_modality_added` presente solo si se
 *  mutó el perfil a 'mixto' al confirmar trail. */
export interface AddRaceResult {
  plan: TrainingPlan;
  replaced_plan_id?: number;
  replaced_plan_type?: string;
  surface_modality_added?: string;
}

/** Sprint 10 S10.1b: payload `postpone` que el backend incluye en la
 *  response cuando el action fue `postpone`. Mirror EXACTO de
 *  backend/services/training_engine/postpone.py::PostponeResult tal
 *  como lo serializa el endpoint (NO se incluye `sessions_skipped_auto`
 *  — derivar de `auto_skipped_ids.length` en el frontend). */
export interface PostponePayload {
  sessions_shifted: number;
  auto_skipped_ids: number[];
  race_date_preserved: boolean;
}

/** Respuesta del PATCH /sessions/<id>.
 *
 * - `adaptation` aparece solo si `action="skip"`.
 * - `postpone` aparece solo si `action="postpone"`.
 * - `fatigue_substitute` (Sprint 10 S10.2b): aparece solo si
 *   `action="report_fatigue"`. Es la TrainingSession nueva creada por
 *   el backend en mismo `session_date` que la original (rod_z2_corto
 *   30 min Z2). La original ya viene serializada en `session` con
 *   `completion_status='skipped'` + `skip_reason='fatigue'`.
 * - `warning="race_modification"` aparece SOLO al posponer un plan con
 *   carrera real (no `personal_challenge`). El backend lo emite al
 *   nivel raíz del response, no anidado en `postpone`. */
export interface PatchSessionResponse {
  session: TrainingSession;
  adaptation?: {
    session_skipped: number;
    adaptation: string;
    alert: { message: string; weeks_incomplete: number } | null;
  };
  postpone?: PostponePayload;
  fatigue_substitute?: TrainingSession;
  warning?: "race_modification";
}

/** Body del POST /training-plans.
 *
 * Sprint 11 S11.1 (motor v8 §1): añadido `start_date` opcional. Si no
 * llega, el backend aplica default `_next_monday(today)` (backward-compat
 * con clientes pre-S11.1). Validaciones server-side: `[today, today+30d]`
 * y `< race_date`. */
export interface CreatePlanPayload {
  race_distance: string;
  /** Sprint 13 S13.3 (motor v10 §2) + Sub-paquete Y v16.5.AB:
   *  - ausente | 'race'   → Plan de Carrera (requiere `race_date`).
   *  - 'progression'       → Plan de Progresión (NO requiere `race_date`;
   *    el backend sintetiza race_date = start + 12 sem).
   *  - 'maintenance'       → Plan de Mantenimiento (NO requiere
   *    `race_distance` ni `race_date`; el backend hereda race_distance del
   *    último plan y sintetiza race_date = start + 2 sem). En la práctica
   *    se usa el payload dedicado `MaintenancePlanPayload`. */
  plan_type?: "race" | "progression" | "maintenance";
  /** Requerido para 'race'; ignorado para 'progression' y 'maintenance'. */
  race_date?: string; // ISO date YYYY-MM-DD
  start_date?: string; // ISO date YYYY-MM-DD (S11.1, opcional)
}

/**
 * Sub-paquete Y v16.5.AB — Payload POST /training-plans plan_type='maintenance'.
 *
 * Maintenance plan NO requiere race_distance (heredado del último plan por
 * el backend, ver `_get_inherited_race_distance` Y.B.3) ni race_date
 * (sintética = start_date + 2 semanas). Solo opcionalmente `start_date`
 * (mismo rango que progression: [today, today+30d]).
 */
export interface MaintenancePlanPayload {
  plan_type: "maintenance";
  start_date?: string; // ISO date YYYY-MM-DD
}

/**
 * Sub-paquete Y v16.5.AB — Response POST /training-plans plan_type='maintenance'.
 *
 * Espejo de la response del endpoint `_create_maintenance_plan` Y.B.4:
 * - `plan`: plan serializado (mismo shape que cualquier TrainingPlan).
 * - `cycle_number`: índice 0-based del ciclo recién creado (0..5). El frontend
 *   muestra ciclo 1..6 al usuario (cycle_number + 1).
 * - `was_reset`: true si el counter previo se reseteó (last NULL o >30d
 *   sin renovar). Permite copy contextual ("Empiezas tras pausa").
 */
export interface MaintenancePlanResponse {
  plan: TrainingPlan;
  cycle_number: number;
  was_reset: boolean;
}

/** Body del PATCH /sessions/<id>.
 *
 * Sprint 10 S10.1b: añadido `action="postpone"`. Para `postpone` NO se
 * envía `reason` ni `notes` (decisión v7.1 §8 "sin pedir motivo").
 * Sprint 10 S10.2b: añadido `action="report_fatigue"`. Tampoco envía
 * `reason` ni `notes` (v7.1 §9 "sin pedir motivo").
 *
 * Sprint 12 S12.7 (motor v9.2 §1): consolidación de mecánicas.
 * - `reason="logistics"` DEPRECADO: backend devuelve 400
 *   `deprecated_skip_logistics`. Usar `action="postpone"` o
 *   `action="move"` en su lugar.
 * - `reason="physical"` sigue válido (dolor / lesión real). */
export type SessionFeeling = "muy_bien" | "bien" | "regular" | "mal";

export interface PatchSessionPayload {
  action: "skip" | "complete_manual" | "postpone" | "report_fatigue";
  /** Solo `physical` aceptado por el backend tras S12.7. */
  reason?: "physical";
  notes?: string;
  /** v14_2 §8: resultados opcionales del modal post-sesión. Solo se
   *  envían con `action="complete_manual"` (rama SIN Strava). Todos
   *  opcionales; el backend valida rangos (HR 40-230, pace 120-900 s/km,
   *  effort 1-5). `actual_avg_pace_seconds` ya en SEGUNDOS (el front
   *  parsea mm:ss→s con `_parsePaceMmSs`). */
  actual_avg_hr?: number;
  actual_max_hr?: number;
  actual_avg_pace_seconds?: number;
  perceived_effort?: number;
  feeling?: SessionFeeling;
}

// =====================================================================
// Onboarding (Sprint 6 Bloque 5)
// Espejo de backend/app/blueprints/onboarding/routes.py.
// =====================================================================

// Sprint 9 S9.4a/b: niveles colapsados a 2 (v7.1).
export type AthleteLevel = "beginner" | "amateur";
// Persisted surface (canónico backend): el input v7.1 usa "ambos" pero
// se normaliza a "mixto" en el endpoint /onboarding antes de persistir.
export type Surface = "asfalto" | "trail" | "mixto";
// Vocabulario v7.1 que el wizard envía al backend (input shape).
export type SurfaceInput = "asfalto" | "trail" | "ambos";
export type DeviceType = "watch_with_hr" | "watch_no_hr" | "mobile_only";
// Sprint 9 S9.3: origen del hr_max para auditoría. 'test_vam' lo
// sobrescribe a posteriori cuando el atleta complete el TEST-VAM.
export type HrMaxSource = "self_reported" | "test_vam" | "estimated";

export interface AthleteProfile {
  id: number;
  user_id: number;
  level: AthleteLevel | null;
  surface_primary: Surface | "pista" | null; // pista existe en BD pero no en onboarding
  training_days_per_week: number | null;
  device_type: DeviceType;
  has_watch: boolean;
  hr_max: number | null;
  hr_max_source: HrMaxSource | null;
  vam_pace_seconds_per_km: number | null;
  vam_m_per_min: number | null;
  last_vam_test_at: string | null;
  /** Sprint 14 v12 Bloque A: zapatilla (informativo, editable sin regenerar). */
  shoe?: string | null;
  /** Sesión 33 EXTENDIDA — Alba feature (Conversación 2 Alberto). UX-only
   *  toggle: si True, `SessionCard` renderiza "Sesión N" en lugar del
   *  día de la semana. Backend NO cambia asignación de `session_date`
   *  (preserva matching Strava). Default false (backward-compat). */
  flexible_scheduling?: boolean;
}

export interface OnboardingStatus {
  completed: boolean;
  profile: AthleteProfile | null;
  missing_fields: string[];
}

// Sprint 9 S9.4b: shape v7.1 del POST /onboarding.
// - level 2-tier (beginner|amateur).
// - days_available 1..7 (backend cappea a MAX_SESSIONS=4 con flag).
// - surface en vocab brief (ambos = mixto persistido).
// - has_watch boolean → backend deriva device_type.
// - fc_max opcional (null si no la conoce); rango 120..220.
export interface OnboardingPayload {
  level: AthleteLevel;
  days_available: number;
  surface: SurfaceInput;
  has_watch: boolean;
  fc_max: number | null;
}

// Sprint 9 S9.4b: response del POST /onboarding.
// - sessions_capped=true cuando days_available > 4 → frontend muestra
//   toast "ajustamos a 4 días".
// - next_action dirige el redirect post-submit del wizard.
export interface OnboardingResponse {
  ok: true;
  profile: AthleteProfile;
  sessions_capped: boolean;
  sessions_per_week: number;
  next_action: "create_plan" | "redirect_home";
}

// =====================================================================
// VAM-result (Sprint 6 Bloque 5)
// =====================================================================

/** Body del POST /training-plans/active/sessions/<id>/vam-result. */
export interface VamResultPayload {
  distance_meters: number;
  // v11 §1: FC media y FC máx OBLIGATORIAS.
  hr_avg: number;
  hr_max: number;
  // v11 §1: ritmo medio reportado (seg/km), OPCIONAL.
  avg_pace_seconds_per_km?: number;
}

export interface VamResultResponse {
  vam_pace_seconds_per_km: number;
  vam_m_per_min: number;
  pace_formatted: string;
  hr_max: number | null;
  message: string;
  session: TrainingSession;
}

/** Error estructurado del backend.
 *
 * Sprint 8 S8.4 (UX-008): cuando un user nuevo intenta autenticarse vía
 * OAuth Google sin invite_code, el backend devuelve 400 con campos
 * extra `oauth_*` para que el frontend pueda redirigir a /register
 * preservando el id_token + email canónico (sin re-OAuth). Los campos
 * son opcionales — solo aparecen en respuestas del flujo OAuth signup.
 */
export interface ApiErrorBody {
  error: string;
  field?: string;
  oauth_pending?: boolean;
  oauth_email?: string;
  oauth_name?: string;
  oauth_provider?: "google" | "apple";
}

/** Argumento opcional del constructor `ApiError` para metadatos OAuth.
 *  Snake_case en transporte → camelCase en JS. */
export interface ApiErrorOAuthMeta {
  oauthPending?: boolean;
  oauthEmail?: string;
  oauthName?: string;
  oauthProvider?: "google" | "apple";
}

/**
 * Error custom que tiramos desde el wrapper fetch.
 * Mantiene el código del backend + status HTTP para que la UI decida copy.
 *
 * Sprint 8 S8.4: expone `oauthPending` + `oauthEmail` + `oauthName` +
 * `oauthProvider` cuando el backend los envía. UI puede pivotar a flow
 * de registro simplificado sin perder la autorización Google ya hecha.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly field: string | undefined;
  public readonly oauthPending: boolean | undefined;
  public readonly oauthEmail: string | undefined;
  public readonly oauthName: string | undefined;
  public readonly oauthProvider: "google" | "apple" | undefined;

  constructor(
    code: string,
    status: number,
    field?: string,
    oauth?: ApiErrorOAuthMeta,
  ) {
    super(code);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.field = field;
    this.oauthPending = oauth?.oauthPending;
    this.oauthEmail = oauth?.oauthEmail;
    this.oauthName = oauth?.oauthName;
    this.oauthProvider = oauth?.oauthProvider;
  }
}
